// 러너 요청 모달에서 마지막으로 고른 러너(daemon)/에이전트 선택을 프로젝트별로 기억한다.
// 러너·에이전트는 프로젝트에 종속된 리소스라 projectId 단위로 저장하고,
// 다시 열 때 해당 선택이 여전히 유효할 때만 기본값으로 복원한다.
export type DaemonTriggerPreference = {
  daemonId?: string;
  agentConfigId?: string;
};

const daemonTriggerPreferencesKey = 'agentteams-daemon-trigger-prefs';

type PreferenceMap = Record<string, DaemonTriggerPreference>;

const readPreferenceMap = (): PreferenceMap => {
  if (typeof window === 'undefined') return {};

  const raw = window.localStorage.getItem(daemonTriggerPreferencesKey);
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed as PreferenceMap;
  } catch {
    return {};
  }
};

const sanitize = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

export const getDaemonTriggerPreference = (projectId: string): DaemonTriggerPreference | null => {
  if (typeof window === 'undefined' || projectId.length === 0) return null;

  const entry = readPreferenceMap()[projectId];
  if (!entry || typeof entry !== 'object') return null;

  return {
    daemonId: sanitize(entry.daemonId),
    agentConfigId: sanitize(entry.agentConfigId),
  };
};

// 같은 프로젝트의 기존 선택과 병합한다(러너만 먼저 잡히고 에이전트가 비어 있는 과도기에
// 기억해 둔 값이 지워지지 않도록).
export const saveDaemonTriggerPreference = (projectId: string, preference: DaemonTriggerPreference) => {
  if (typeof window === 'undefined' || projectId.length === 0) return;

  const map = readPreferenceMap();
  const previous = map[projectId] ?? {};
  const next: DaemonTriggerPreference = {
    daemonId: sanitize(preference.daemonId) ?? sanitize(previous.daemonId),
    agentConfigId: sanitize(preference.agentConfigId) ?? sanitize(previous.agentConfigId),
  };

  map[projectId] = next;

  try {
    window.localStorage.setItem(daemonTriggerPreferencesKey, JSON.stringify(map));
  } catch {
    // localStorage 비활성/할당량 초과 환경에서는 기억 기능 없이도 동작해야 한다.
  }
};
